/**
 * Messages in a project (Team › Messages, the In this Project panel): a chat between the people of the project, over
 * the notes of vfs-service (src/collab/notes.js; people's messages: src/collab/chat.js, `POST /collab/messages`). Pure
 * helpers: which conversation a note belongs to, the conversations with their last message and unread count, days and
 * runs of bubbles, mentions, and what is read.
 *
 * Conversations:
 * - Everyone ('everyone'): the project's room, notes to 'people' (and the older "Everyone" pairs, people + agents).
 * - One per colleague ('dm:<their email>'): notes from me to them and from them to me. A note between two others is
 *   not shown (it is theirs).
 * - AI agents ('agents'): notes for the agents and every note an agent wrote ("Tell the agent", agents' notes).
 *
 * The server's rules this mirrors:
 * - Anyone who can open the project sends to Everyone or one person (a viewer too); a note for the AI agents needs an
 *   editor (`POST /collab/notes`). Text: at most MESSAGE_MAX characters.
 * - A direct message notifies its recipient; an Everyone message only the people it @mentions (`@first name`).
 * - Unread is kept in this browser, per conversation (`flow.messages.read.<project>`): what came from someone else after
 *   the conversation was last on the screen.
 */

export const MESSAGE_MAX = 2000;
export const EVERYONE = 'everyone';
export const AGENTS = 'agents';
const EVERYONE_GAP_MS = 10000;
const RUN_GAP_MS = 5 * 60 * 1000;

const lower = (value) => String(value || '').toLowerCase();
export const firstNameOf = (name) => String(name || '').split(/\s+/)[0] || 'Someone';

/** Is this note's writer the signed-in person (`me`: `{ sub, email }`)? An agent's writer is its driver. */
export function writtenBy(note, me) {
  if (!note?.by || !me) { return false; }
  if (me.sub && note.by.sub && note.by.sub === me.sub) { return true; }
  return Boolean(me.email) && note.by.id === `email:${lower(me.email)}`;
}

/** The members of the project (`/access`: owner and members), each `{ email, name, sub, role }`, without `me`. */
export function membersOf(access, me = null) {
  const seen = new Set();
  const out = [];
  [access?.owner, ...(access?.members || [])].forEach((person) => {
    if (!person?.email) { return; }
    const email = lower(person.email);
    if (seen.has(email)) { return; }
    seen.add(email);
    if (me && ((me.sub && person.sub && person.sub === me.sub) || (me.email && lower(me.email) === email))) { return; }
    out.push({ email, name: person.name || email.split('@')[0], sub: person.sub || null, role: person === access?.owner ? 'owner' : person.role || null });
  });
  return out;
}

/** "Claude Code for Alice", "Flow chat for you". */
export function agentLabel(agent, me = null) {
  const label = agent?.label || 'AI agent';
  const driver = agent?.driver;
  if (!driver?.name) { return label; }
  const mine = me && ((me.sub && driver.sub === me.sub) || (me.presenceId && driver.id === me.presenceId));
  return `${label} for ${mine ? 'you' : firstNameOf(driver.name)}`;
}

/** The AI agents one can write to: here now (presence) first, then the ones that wrote notes. `[{ agentId, label, online }]`. */
export function agentOptions({ snapshot = null, notes = [], me = null, knownAgents = [] } = {}) {
  const agents = new Map();
  const here = new Set((snapshot?.agents || []).map((agent) => agent?.id).filter(Boolean));
  [...(snapshot?.agents || []), ...(knownAgents || [])].forEach((agent) => {
    if (agent?.id && !agents.has(agent.id)) {
      agents.set(agent.id, { agentId: agent.id, label: agentLabel(agent, me && { ...me, presenceId: snapshot?.you?.id }), online: here.has(agent.id) });
    }
  });
  (notes || []).forEach((note) => {
    const id = note?.by?.kind === 'agent' ? note.by.agentId : null;
    if (id && !agents.has(id)) {
      agents.set(id, { agentId: id, label: writtenBy(note, me) ? `${note.by.label || 'AI agent'} for you` : note.from || 'AI agent', online: false });
    }
  });
  return [...agents.values()].sort((a, b) => Number(b.online) - Number(a.online) || a.label.localeCompare(b.label));
}

/** "a.jsx:12", "a.jsx". */
export function placeLabel(place) {
  if (!place?.path) { return ''; }
  const name = String(place.path).split('/').pop();
  return Number.isInteger(place.line) ? `${name}:${place.line}` : name;
}

/**
 * The notes oldest first (newest last), an older "Everyone" send (the same writer and text, one note to the people and
 * one to the agents, sent together) as one row with `to: 'everyone'` and both `ids`.
 */
export function conversation(notes) {
  const ordered = [...(notes || [])].sort((a, b) => String(a.at).localeCompare(String(b.at)));
  const out = [];
  ordered.forEach((note) => {
    const last = out[out.length - 1];
    const pair = last && last.to !== 'everyone' && !last.agent && !note.agent
      && ((last.to === 'people' && note.to === 'agents') || (last.to === 'agents' && note.to === 'people'))
      && last.text === note.text && last.by?.id === note.by?.id && last.by?.agentId === note.by?.agentId
      && Math.abs(Date.parse(note.at) - Date.parse(last.at)) <= EVERYONE_GAP_MS;
    if (pair) {
      out[out.length - 1] = { ...last, to: 'everyone', ids: [last.id, note.id] };
    } else {
      out.push({ ...note, ids: [note.id] });
    }
  });
  return out;
}

/** The address of a person's note's writer: from `by.id` ('email:…') or their `sub` among the members; else null. */
function writerEmail(note, members) {
  if (String(note.by?.id || '').startsWith('email:')) { return note.by.id.slice(6); }
  const member = note.by?.sub ? (members || []).find((m) => m.sub && m.sub === note.by.sub) : null;
  return member ? member.email : null;
}

/** The conversation a note belongs to for `me` (see the file's comment), or null when it is not mine to see. */
export function conversationKey(note, { me = null, members = [] } = {}) {
  if (!note) { return null; }
  if (note.by?.kind === 'agent' || note.to === 'agents') { return AGENTS; }
  if (note.to === 'people' || note.to === 'everyone') { return EVERYONE; }
  const to = lower(note.to);
  if (writtenBy(note, me)) { return `dm:${to}`; }
  if (me?.email && to === lower(me.email)) {
    const from = writerEmail(note, members);
    return from ? `dm:${from}` : `dm:sub:${note.by?.sub || 'unknown'}`;
  }
  return null;
}

/** Is this message for me: to everyone (people) or to my address, and not mine? */
export function isForMe(note, me) {
  if (!me || writtenBy(note, me)) { return false; }
  return note.to === 'people' || note.to === 'everyone' || (Boolean(me.email) && note.to === lower(me.email));
}

/**
 * Unread: someone else's, in a conversation of mine (in AI agents: for the people or for me), newer than when that
 * conversation was last read. `seen`: the read marks (readMarks()) or, as before, one time for the whole project.
 */
export function isUnread(note, me, seen, { members = [] } = {}) {
  if (!me || writtenBy(note, me)) { return false; }
  const key = conversationKey(note, { me, members });
  if (!key || (key === AGENTS && !isForMe(note, me))) { return false; }
  const mark = seen && typeof seen === 'object' ? (seen.marks?.[key] || seen.at || null) : seen;
  return !mark || String(note.at) > String(mark);
}

/**
 * The conversations of `me`: Everyone, one per colleague (the members, then anyone a message came from who is not
 * listed any more), AI agents. Each `{ key, kind, label, email?, role?, messages (oldest first), last, unread }`, the
 * ones with the newest messages first after Everyone; AI agents is listed when it has messages or `withAgents`.
 */
export function conversationsOf(notes, { me = null, members = [], seen = null, withAgents = false } = {}) {
  const rows = conversation(notes || []);
  const byKey = new Map();
  const add = (key, info) => { if (!byKey.has(key)) { byKey.set(key, { key, messages: [], ...info }); } return byKey.get(key); };
  add(EVERYONE, { kind: 'everyone', label: 'Everyone' });
  (members || []).forEach((m) => add(`dm:${m.email}`, { kind: 'person', label: m.name, email: m.email, role: m.role || null }));
  rows.forEach((note) => {
    const key = conversationKey(note, { me, members });
    if (!key) { return; }
    const info = key === AGENTS ? { kind: 'agents', label: 'AI agents' }
      : { kind: 'person', label: writtenBy(note, me) ? note.to : note.from || note.by?.name || 'Someone', email: key.startsWith('dm:sub:') ? null : key.slice(3), gone: true };
    add(key, info).messages.push(note);
  });
  if (withAgents) { add(AGENTS, { kind: 'agents', label: 'AI agents' }); }
  const list = [...byKey.values()].map((c) => ({
    ...c,
    last: c.messages[c.messages.length - 1] || null,
    unread: c.messages.filter((note) => isUnread(note, me, seen, { members })).length,
  }));
  const everyone = list.find((c) => c.key === EVERYONE);
  const rest = list.filter((c) => c.key !== EVERYONE)
    .sort((a, b) => String(b.last?.at || '').localeCompare(String(a.last?.at || '')) || a.label.localeCompare(b.label));
  return [everyone, ...rest];
}

/** The option a caller of openMessages asked for, as a conversation: `{ key, agentId? }` (see Messages.jsx). */
export function conversationFor(recipient, members = []) {
  if (!recipient || recipient === 'everyone' || recipient === 'people') { return { key: EVERYONE }; }
  if (recipient === 'agents') { return { key: AGENTS }; }
  if (recipient.startsWith('agent:')) { return { key: AGENTS, agentId: recipient.slice(6) }; }
  if (recipient.startsWith('person:')) { return { key: `dm:${lower(recipient.slice(7))}` }; }
  if (recipient.startsWith('sub:')) {
    const member = (members || []).find((m) => m.sub && m.sub === recipient.slice(4));
    return member ? { key: `dm:${member.email}` } : null;
  }
  return { key: EVERYONE };
}

/** Who wrote it, as a bubble says it: "You", "Claude Code for you", "Alice Rossi", "Flow chat for Luca". */
export function senderLabel(note, me) {
  if (writtenBy(note, me)) { return note.by?.kind === 'agent' ? `${note.by.label || 'AI agent'} for you` : 'You'; }
  return note.from || note.by?.name || 'Someone';
}

/** In AI agents: who a note was for ("to the AI agents", "to Claude Code for Alice", "to everyone"); null elsewhere. */
export function agentRecipientText(note, agents = []) {
  if (note.to === 'everyone') { return 'to everyone'; }
  if (note.to === 'people') { return 'to the people'; }
  if (note.to !== 'agents') { return null; }
  if (!note.agent) { return 'to the AI agents'; }
  const known = (agents || []).find((a) => a.agentId === note.agent);
  return `to ${known ? known.label : 'one AI agent'}`;
}

const dayOf = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

/** "Today", "Yesterday", "Mon 26 Oct", "26 Oct 2025". */
export function dayLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) { return ''; }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayOf(iso) === dayOf(now.toISOString())) { return 'Today'; }
  if (dayOf(iso) === dayOf(yesterday.toISOString())) { return 'Yesterday'; }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { weekday: 'short', day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "14:05", in the browser's own clock. */
export const timeLabel = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

/**
 * The messages of one conversation as the chat shows them: `[{ day, label, items: [{ note, first, mine }] }]`, oldest
 * first; `first`: the first of a run by the same writer (within 5 minutes), where the name shows.
 */
export function chatDays(messages, me, now = new Date()) {
  const days = [];
  let prev = null;
  (messages || []).forEach((note) => {
    const day = dayOf(note.at);
    if (!days.length || days[days.length - 1].day !== day) {
      days.push({ day, label: dayLabel(note.at, now), items: [] });
      prev = null;
    }
    const writer = `${note.by?.id || ''}|${note.by?.agentId || ''}`;
    const first = !prev || prev.writer !== writer || Date.parse(note.at) - Date.parse(prev.at) > RUN_GAP_MS;
    days[days.length - 1].items.push({ note, first, mine: writtenBy(note, me) && note.by?.kind !== 'agent' });
    prev = { writer, at: note.at };
  });
  return days;
}

/** The text in pieces, `@name` mentions marked: `[{ text, mention }]`. */
export function mentionParts(text) {
  return String(text || '').split(/(@[\p{L}\p{N}][\p{L}\p{N}._-]*)/u).filter(Boolean).map((part) => ({ text: part, mention: part.startsWith('@') }));
}

/** May this person send messages to people here? Anyone who can open the project (a viewer too). */
export const canSendMessages = (role) => role !== 'none';

/** May this person write to the AI agents? Not a viewer (vfs-service refuses their note). Unknown role: yes. */
export const canMessageAgents = (role) => role !== 'viewer' && role !== 'none';

const SEEN_KEY = (projectId) => `flow.messages.seen.${projectId}`;
const READ_KEY = (projectId) => `flow.messages.read.${projectId}`;

/** When I last opened Messages in this project (this browser, before conversations had their own marks), or null. */
export function readSeenAt(projectId) {
  try { return window.localStorage.getItem(SEEN_KEY(projectId)) || null; } catch { return null; }
}

/** The read marks of this browser: `{ marks: { [conversation]: iso }, at: readSeenAt() }` (isUnread's `seen`). */
export function readMarks(projectId) {
  let marks = {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(READ_KEY(projectId)) || '{}');
    marks = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch { /* none kept */ }
  return { marks, at: readSeenAt(projectId) };
}

/** That conversation is read up to `at` (kept only forward). Answers whether the mark moved. */
export function writeReadMark(projectId, key, at) {
  if (!key || !at) { return false; }
  try {
    const { marks } = readMarks(projectId);
    if (marks[key] && String(marks[key]) >= String(at)) { return false; }
    window.localStorage.setItem(READ_KEY(projectId), JSON.stringify({ ...marks, [key]: at }));
    return true;
  } catch { return false; /* private window: nothing kept */ }
}
